import React from 'react';
import { FormApi, FormState } from 'final-form';
import { useDispatch } from 'react-redux';
import { Form, FormProps, FormRenderProps } from 'react-final-form';

import FormKeys from 'modules/form/types/FormKeys';

import getDecorator from './decorator';
import { setFormState } from './formMutator';
import useInitialValues from './useInitialValues';

type ConnectedFormWrapperProps = {
  componentChild: React.ElementType;
  errorComponent?: React.ReactElement<{ onClose: () => void; formState: FormState<any> }>;
} & ({ formKey: FormKeys; formKeys?: undefined; } | { formKey?: undefined; formKeys: Array<FormKeys>; });

const ConnectedFormWrapper = <T,>({
  errorComponent: ErrorComponent, componentChild: Component, handleSubmit: handleSubmitProp, ...props
}: FormRenderProps<T> & ConnectedFormWrapperProps): React.ReactElement<any> => {
  const { form } = props;

  const [displayError, setDisplayError] = React.useState(false);

  const handleSubmit = React.useCallback(() => {
    if (ErrorComponent && form.getState().hasValidationErrors) {
      setDisplayError(true);
      form.mutators.setFormState('submitFailed', true);
    } else {
      handleSubmitProp?.();
    }
  }, [ErrorComponent, form, handleSubmitProp]);

  return (
    <>
      <Component {...props} handleSubmit={handleSubmit} />
      {displayError && React.cloneElement(ErrorComponent, {
        onClose: () => setDisplayError(false),
        formState: form.getState(),
      })}
    </>
  );
};

type ConnectedFormProps<T> = Omit<ConnectedFormWrapperProps, 'componentChild'> & FormProps<T>;

const ConnectedForm = <T extends { [key in FormKeys]: U } | U, U>({
  formKey, formKeys: formKeyProp, onSubmit, component, mutators, ...props
}: ConnectedFormProps<T>): React.ReactElement<any> => {
  const dispatch = useDispatch();

  const formKeys = React.useMemo(
    () => (formKey ? [formKey] : formKeyProp) as Array<FormKeys>,
    [formKey, formKeyProp],
  );

  const initialValue = useInitialValues<T, U>(...formKeys);

  const decorators = React.useMemo(() => (
    [...(props.decorators ?? []), getDecorator<T>(formKeys, dispatch)]
  ), [formKeys, dispatch, props.decorators]);

  // async is required to trigger submitting form subscription in the decorator and thus persist the form on submit
  const handleSubmit = React.useCallback(
    async (values: T, form: FormApi<T>) => onSubmit(values, form),
    [onSubmit],
  );

  return (
    <Form<T>
      {...props}
      formKey={formKey}
      decorators={decorators}
      onSubmit={handleSubmit}
      componentChild={component}
      initialValues={initialValue}
      component={ConnectedFormWrapper}
      mutators={{ ...mutators, setFormState }}
    />
  );
};

export default ConnectedForm;
